import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, TrendingUp, Handshake, DollarSign, User, Settings, LogOut, LayoutDashboard, Home, Info, CircleHelp, GraduationCap, Calendar, Briefcase, ShoppingBag, Wallet, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import AdBanner from "../landing/AdBanner";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";

const navItems = [
  { label: "الرئسية", labelEn: "Home", path: "/", icon: Home },
  { label: "من نحن", labelEn: "About Us", path: "/about", icon: Info },
  { label: "تسير المنصة", labelEn: "How It Works", path: "/how-it-works", icon: CircleHelp },
  { label: "الخبراء", labelEn: "Experts", path: "/experts", icon: GraduationCap },
  { label: "فتح المؤسسة في 6 أشهر", labelEn: "6-Month Program", path: "/program", icon: Calendar },
  { label: "مشاريع الاستثمار", labelEn: "Projects", path: "/projects", icon: Briefcase },
  { label: "المشاريع للبيع", labelEn: "Project Sales", path: "/project-sales", icon: ShoppingBag },
  { label: "المستثمرين", labelEn: "Investors", path: "/investor", icon: TrendingUp },
  { label: "شركاؤنا", labelEn: "Our Partners", path: "/partners", icon: Handshake },
  { label: "البدائل المالية", labelEn: "Financial Alternatives", path: "/financial-alternatives", icon: Wallet },
  { label: "الإنجازات", labelEn: "Achievements", path: "/achievements", icon: Trophy },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userType, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();

  // Check if we're on a dashboard route
  const isDashboardRoute = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/admin') ||
    location.pathname === '/projects/new';

  const isActive = (path: string) => location.pathname === path;

  // Don't render navbar on dashboard routes
  if (isDashboardRoute) {
    return null;
  }

  return (
    <nav className="sticky top-0 z-50 animate-fade-in-down">
      {/* Top Green Header */}
      <div className="bg-primary">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 gap-4 ltr:flex-row-reverse">
            {/* Login/Logout Button - Left Side */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-transparent focus:ring-0 px-0 sm:px-2">
                    <span className="text-white text-sm hidden sm:inline font-normal">
                      {language === "ar" ? `مرحباً، ${user.name}` : `Welcome, ${user.name}`}
                    </span>
                    <Avatar className="h-10 w-10 border-2 border-white/20 transition-all hover:border-white/40">
                      <AvatarImage src="" alt={user.name} />
                      <AvatarFallback className="bg-primary-foreground/10 text-primary-foreground font-medium">
                        {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email || ""}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(userType === "admin" ? "/admin" : userType === "investor" ? "/dashboard/investor" : "/dashboard/project-holder")} className="cursor-pointer">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    <span>{language === "ar" ? "لوحة القيادة" : "Dashboard"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>{language === "ar" ? "الإعدادات" : "Settings"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{language === "ar" ? "تسجيل خروج" : "Log out"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button
                  variant="outline"
                  className="bg-card hover:bg-card/90 text-primary border-card px-6 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105"
                >
                  {language === "ar" ? "تسجيل دخول" : "Login"}
                </Button>
              </Link>
            )}

            {/* Ad Banner - Between Logo and Login Button */}
            <div className="flex-1 h-12">
              <AdBanner />
            </div>

            {/* Logo - Right Side */}
            <Link to="/home" className="flex items-center gap-3 group">
              <div className="text-right hidden sm:block">
              </div>
              <div className="w-150 h-12 bg-card rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 p-1">
                <img src="/logo.png" alt="Logo" className="w-150 h-full object-contain" />

              </div>
            </Link>

            {/* Language Toggle - Beside Logo */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="rounded-full w-8 h-8 bg-white hover:bg-gray-100 hidden sm:flex items-center justify-center transition-all duration-300 hover:scale-105 overflow-hidden shadow-sm p-0.5"
              title={language === "ar" ? "Switch to English" : "Switch to Arabic"}
            >
              {language === "ar" ? (
                // US Flag SVG
                <svg viewBox="0 0 640 480" className="w-full h-full object-cover rounded-full border border-black/10">
                  <path fill="#bd3d44" d="M0 0h640v480H0" />
                  <path stroke="#fff" strokeWidth="37" d="M0 55.3h640M0 129h640M0 202.8h640M0 276.5h640M0 350.2h640M0 424h640" />
                  <path fill="#192f5d" d="M0 0h296v258.5H0" />
                  <marker id="us-a" markerHeight="30" markerWidth="30">
                    <path fill="#fff" d="M14.7 0l2.5 8.7h8.8l-7 5.2 2.6 8.7-7-5-7.2 5 2.6-8.7-7.1-5.2h8.8" />
                  </marker>
                  <use xlinkHref="#us-a" x="24.6" y="22" />
                  <use xlinkHref="#us-a" x="65.6" y="22" />
                  <use xlinkHref="#us-a" x="106.6" y="22" />
                  <use xlinkHref="#us-a" x="147.6" y="22" />
                  <use xlinkHref="#us-a" x="188.6" y="22" />
                  <use xlinkHref="#us-a" x="229.6" y="22" />
                  <use xlinkHref="#us-a" x="270.6" y="22" />
                  <use xlinkHref="#us-a" x="45.1" y="47" />
                  <use xlinkHref="#us-a" x="86.1" y="47" />
                  <use xlinkHref="#us-a" x="127.1" y="47" />
                  <use xlinkHref="#us-a" x="168.1" y="47" />
                  <use xlinkHref="#us-a" x="209.1" y="47" />
                  <use xlinkHref="#us-a" x="250.1" y="47" />
                  <use xlinkHref="#us-a" x="24.6" y="73" />
                  <use xlinkHref="#us-a" x="65.6" y="73" />
                  <use xlinkHref="#us-a" x="106.6" y="73" />
                  <use xlinkHref="#us-a" x="147.6" y="73" />
                  <use xlinkHref="#us-a" x="188.6" y="73" />
                  <use xlinkHref="#us-a" x="229.6" y="73" />
                  <use xlinkHref="#us-a" x="270.6" y="73" />
                  <use xlinkHref="#us-a" x="45.1" y="98" />
                  <use xlinkHref="#us-a" x="86.1" y="98" />
                  <use xlinkHref="#us-a" x="127.1" y="98" />
                  <use xlinkHref="#us-a" x="168.1" y="98" />
                  <use xlinkHref="#us-a" x="209.1" y="98" />
                  <use xlinkHref="#us-a" x="250.1" y="98" />
                  <use xlinkHref="#us-a" x="24.6" y="124" />
                  <use xlinkHref="#us-a" x="65.6" y="124" />
                  <use xlinkHref="#us-a" x="106.6" y="124" />
                  <use xlinkHref="#us-a" x="147.6" y="124" />
                  <use xlinkHref="#us-a" x="188.6" y="124" />
                  <use xlinkHref="#us-a" x="229.6" y="124" />
                  <use xlinkHref="#us-a" x="270.6" y="124" />
                  <use xlinkHref="#us-a" x="45.1" y="149" />
                  <use xlinkHref="#us-a" x="86.1" y="149" />
                  <use xlinkHref="#us-a" x="127.1" y="149" />
                  <use xlinkHref="#us-a" x="168.1" y="149" />
                  <use xlinkHref="#us-a" x="209.1" y="149" />
                  <use xlinkHref="#us-a" x="250.1" y="149" />
                  <use xlinkHref="#us-a" x="24.6" y="174" />
                  <use xlinkHref="#us-a" x="65.6" y="174" />
                  <use xlinkHref="#us-a" x="106.6" y="174" />
                  <use xlinkHref="#us-a" x="147.6" y="174" />
                  <use xlinkHref="#us-a" x="188.6" y="174" />
                  <use xlinkHref="#us-a" x="229.6" y="174" />
                  <use xlinkHref="#us-a" x="270.6" y="174" />
                  <use xlinkHref="#us-a" x="45.1" y="199" />
                  <use xlinkHref="#us-a" x="86.1" y="199" />
                  <use xlinkHref="#us-a" x="127.1" y="199" />
                  <use xlinkHref="#us-a" x="168.1" y="199" />
                  <use xlinkHref="#us-a" x="209.1" y="199" />
                  <use xlinkHref="#us-a" x="250.1" y="199" />
                  <use xlinkHref="#us-a" x="24.6" y="225" />
                  <use xlinkHref="#us-a" x="65.6" y="225" />
                  <use xlinkHref="#us-a" x="106.6" y="225" />
                  <use xlinkHref="#us-a" x="147.6" y="225" />
                  <use xlinkHref="#us-a" x="188.6" y="225" />
                  <use xlinkHref="#us-a" x="229.6" y="225" />
                  <use xlinkHref="#us-a" x="270.6" y="225" />
                </svg>
              ) : (
                // Algeria Flag SVG
                <svg viewBox="0 0 900 600" className="w-full h-full object-cover rounded-full border border-black/10">
                  <rect width="900" height="600" fill="#fff" />
                  <rect width="450" height="600" fill="#006233" />
                  <circle cx="450" cy="300" r="120" fill="#d21034" />
                  <circle cx="470" cy="300" r="100" fill="#fff" />
                  <path fill="#006233" d="M470 200 A100 100 0 0 0 470 400 A100 100 0 0 0 470 200 Z" style={{ display: 'none' }} />
                  <path fill="#d21034" transform="translate(490,300) rotate(-90) scale(70)" d="M0,-1 L0.588,0.809 -0.951,-0.309 0.951,-0.309 -0.588,0.809 Z" />
                  {/* Correcting masking visually by overlaying simplified shapes */}
                  <rect width="450" height="600" fill="#006233" />
                  <rect x="450" width="450" height="600" fill="#fff" />
                  <path fill="#d21034" d="M508.6 300c0 62.7-44.9 114.9-104.4 125.6 15.6 2.9 31.7 4.4 48.2 4.4 82.8 0 150-67.2 150-150s-67.2-150-150-150c-16.5 0-32.6 1.5-48.2 4.4 59.5 10.7 104.4 62.9 104.4 125.6z" />
                  <path fill="#d21034" transform="translate(520,300) rotate(-18)" d="M0-60l13.4 41.2H56.8L21.7 6.6l13.4 41.2-35.1-25.5-35.1 25.5 13.4-41.2L-56.8 6.6h43.4z" />
                </svg>
              )}
            </Button>

            {/* Mobile Menu Button - Right Side */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Links */}
      <div className="bg-card border-b border-border shadow-sm overflow-x-auto">
        <div className="container mx-auto px-4">
          <div className="hidden lg:flex items-center justify-center gap-1 py-3 min-w-max">
            {navItems.map((item, index) => (
              <Link
                key={item.path + index}
                to={item.path}
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2 ${isActive(item.path)
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                  }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{language === "ar" ? item.label : item.labelEn}</span>
              </Link>
            ))}




          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`lg:hidden bg-card overflow-hidden transition-all duration-300 ease-out ${isOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="py-4 px-4 border-b border-border max-h-[60vh] overflow-y-auto">


          <div className="flex flex-col gap-1">
            {navItems.map((item, index) => (
              <Link
                key={item.path + index}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-3 ${isActive(item.path)
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                  }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{language === "ar" ? item.label : item.labelEn}</span>
              </Link>
            ))}

            {/* Mobile Dashboard Link (if user is logged in) */}
            {user && userType && (
              <Link
                to={userType === "admin" ? "/admin" : userType === "investor" ? "/dashboard/investor" : "/dashboard/project-holder"}
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 ${isActive(userType === "admin" ? "/admin" : userType === "investor" ? "/dashboard/investor" : "/dashboard/project-holder")
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                  }`}
              >
                {userType === "admin" ? (language === "ar" ? "لوحة الإدارة" : "Admin Dashboard") : userType === "investor" ? (language === "ar" ? "لوحة المستثمر" : "Investor Dashboard") : (language === "ar" ? "لوحة المشروع" : "Project Dashboard")}
              </Link>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-xs w-full sm:w-auto"
              >
                {language === "ar" ? "EN" : "عربي"}
              </Button>
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                    navigate("/");
                    setIsOpen(false);
                  }}
                  className="text-xs w-full sm:w-auto"
                >
                  {language === "ar" ? "تسجيل خروج" : "Logout"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}